<script setup lang="ts">
import {
  Anagram,
  Link,
  Logo,
  TheLanguageSelector,
  BugReport,
  AvatarCompact,
  IconButton,
} from "@pohlcon/design-system";
import { useI18n } from "vue-i18n";
import { MessageSchema } from "~/locales";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { CreateErrorReportDto } from "~/api/webservice";
import { errorReportAPI } from "~/APIConnection";
import { Toast } from "./";
import { useAuthStore } from "~/store/auth.store";

const props = defineProps<{
  isEmbed: boolean;
}>();

const authStore = useAuthStore();
const router = useRouter();

const isAuthenticated = computed(() => authStore.isAuthenticated);
const userName = computed(
  () => authStore.userData.firstName + " " + authStore.userData.lastName,
);
const open = ref(false);

const route = useRoute();
const { t, locale } = useI18n<{ messages: MessageSchema; }>();
const logos = ["HBAU", "JORDAHL", "PUK"];
const showBugReportModal = ref(false);
const bugTicketId = ref<number | null>(null);

const bugTicketText = computed({
  get: () =>
    bugTicketId.value
      ? t("bugReport.confirmation", { ticketId: bugTicketId.value })
      : "",
  set: () => null,
});

const bugTicketError = ref("");

const handleBugReport = async (
  bugReport: string,
  userData: { mail: string; name: string; },
) => {
  const currentRoute = `Gesendet von Route: '${route.fullPath}'<br/><br/>`;

  const body: CreateErrorReportDto = {
    description: currentRoute + "\n\n" + bugReport,
  };
  Object.keys(userData).forEach((key) => {
    const typedKey = key as keyof typeof userData;
    if (!!userData[typedKey]) {
      body[(key === "mail" ? "email" : key) as keyof CreateErrorReportDto] =
        userData[typedKey];
    }
  });

  return errorReportAPI
    .errorReportControllerReportError({
      createErrorReportDto: body as CreateErrorReportDto,
    })
    .then((response: any) => {
      showBugReportModal.value = false;
      bugTicketId.value = response.data.id;
    })
    .catch((error) => {
      bugTicketError.value = error.response.data.message.join(" and\n ");
    });
};

const handleBackgroundClick = (e: MouseEvent) => {
  console.log(e);
  if (e.target instanceof HTMLElement && e.target.classList.contains('background_overlay')) {
    open.value = false;
  }
};

function toggle () {
  open.value = !open.value;
}

function logout () {
  authStore.logout();
  router.push({ name: "Home" });
}
</script>

<template>
  <header id="header">
    <div class="brands">
      <Link
        :to="`https://digital.pohlcon.com/?lang=${locale}`"
        class="anagram print__hide"
        :tooltipPosition="'bottom-center'"
        :target="'_blank'"
      >
        <Anagram />
      </Link>
      <span>|</span>
      <div class="logos">
        <Logo
          v-for="logo in logos"
          :key="logo"
          :brand="logo"
          :class="{ grey: logo === 'HBAU' ? false : true }"
        />
      </div>
    </div>
    <div class="header-actions">
      <!-- <BugReport
        v-model:openModal="showBugReportModal"
        :getUserData="true"
        :tooltip="t('bugReport.buttonTooltip')"
        :apiSubmitCallback="(bugReport: string, userData: { mail: string; name: string; }) => handleBugReport(bugReport, userData)"
      /> -->
      <TheLanguageSelector
        v-if="!isEmbed"
        :noMetaTranslate="true"
      />
      <div
        v-if="isAuthenticated"
        class="list__headerLanguage"
      >
        <AvatarCompact
          class="list__avatar"
          :userName="userName"
          type="default"
          tooltipPosition="top-left"
          @click="toggle"
        />
        <div
          v-if="open"
          class="dropdown"
        >
          <IconButton
            icon="export"
            @click="logout"
            :tooltip="'Logout'"
            :tooltipPosition="'top-left'"
          />
        </div>
        <div
          v-if="open"
          class="background_overlay"
          @click.stop.prevent="handleBackgroundClick"
        />
      </div>
    </div>
  </header>
  <Teleport :to="'#toast-container'">
    <Toast
      v-show="bugTicketId"
      v-model="bugTicketText"
      :state="'success'"
    />
    <Toast
      v-show="bugTicketError"
      v-model="bugTicketError"
      :state="'error'"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@use "@pohlcon/design-system/index.scss" as *;

#header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .brands {
    @extend %h5-sub-section;
    display: flex;
    gap: var(--gutter-s);
    color: var(--color-grey-70);
    align-items: center;

    figure.anagram {
      height: 16px;
    }

    .logos {
      display: flex;
      gap: var(--gutter-s);

      :deep(figure.logo) {
        height: var(--gutter-m);
        width: auto;

        &.grey {
          svg .primary {
            fill: var(--color-grey-80) !important;
          }
        }
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: var(--gutter-m);

    .list__avatar {
      cursor: pointer;
    }

    .list__headerLanguage {
      position: relative;
      display: flex;
      align-items: center;
    }

    .dropdown {
      position: absolute;
      cursor: pointer;
      top: 100%;
      margin-top: var(--gutter-2xs);
      right: 0px;
      z-index: 100;
      background-color: var(--color-white);
      border-radius: var(--gutter-xs);
      box-shadow: var(--elevation-2);
    }

    .background_overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 20;
    }
  }
}
</style>
